import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styles from "../LegalIdVerification.module.scss";
import { usePageView } from "../../../shared/analytics/usePageView";
import { trackClicked } from "../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../shared/analytics/documentCaptureEvents";
import type { ConsumerConfig } from "../../../consumers";
import { PartnerFooter } from "./PartnerFooter";
import { WhiteLabelIntroScreen } from "./WhiteLabelIntroScreen";
import { useShowPartnerFooter } from "../../brandLogo/BrandLogo";

interface IntroScreenProps {
  onOpenCamera: () => void;
  onFallback: () => void;
  fallbackAvailable: boolean;
  consumerLogo?: { src: string; alt: string };
  consumerConfig?: ConsumerConfig | null;
  /** Extra actions under Open camera, supplied by the host (e.g. the mock's Skip Camera). */
  children?: ReactNode;
}

/**
 * Legal ID landing screen. Renders the IDMe-branded (green label) layout; a
 * white-label consumer with its own Legal ID copy (consumerConfig.legalIdIntro)
 * gets WhiteLabelIntroScreen instead. The intro analytics live here so both
 * layouts report the same events.
 */
export function IntroScreen({
  onOpenCamera,
  onFallback,
  fallbackAvailable,
  consumerLogo,
  consumerConfig,
  children,
}: IntroScreenProps) {
  const { t } = useTranslation();
  const showPartnerFooter = useShowPartnerFooter(consumerLogo);
  const introCopy = consumerConfig?.legalIdIntro;
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.INFO_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INFO });

  const handleOpenCamera = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.START_DOCUMENT_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "open" });
    onOpenCamera();
  };

  const handleFallback = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.REPORT_NO_DOCUMENT, "link", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "navigate" });
    onFallback();
  };

  if (introCopy) {
    return (
      <WhiteLabelIntroScreen
        introCopy={introCopy}
        onOpenCamera={handleOpenCamera}
        onFallback={handleFallback}
        fallbackAvailable={fallbackAvailable}
        showPartnerFooter={showPartnerFooter}
      >
        {children}
      </WhiteLabelIntroScreen>
    );
  }

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>{t('legalId.ready.heading')}</h1>

      <div className={styles.docIllustration}>
        <img src="/assets/svg/legal-id-image.svg" alt={t('legalId.docIcon.alt')} width={219} height={172} />
      </div>

      <ul className={styles.infoList}>
        <li className={styles.infoItem}>
          <img src="/assets/svg/icon-id-card.svg" alt="" aria-hidden="true" className={styles.infoIcon} />
          <span className={styles.infoText}>{t('legalId.ready.info1')}</span>
        </li>
        <li className={styles.infoItem}>
          <img src="/assets/svg/icon-check-box.svg" alt="" aria-hidden="true" className={styles.infoIcon} />
          <span className={styles.infoText}>{t('legalId.ready.info2')}</span>
        </li>
      </ul>

      <button className={styles.openCameraButton} onClick={handleOpenCamera}>
        {t('legalId.ready.button')}
      </button>

      {children}

      {showPartnerFooter && <PartnerFooter />}
    </div>
  );
}
